<div class="relative flex h-full w-full overflow-hidden bg-slate-100">
    <!-- Chat list (background) -->
    <div class="flex h-full w-full flex-col gap-3 p-3">
        <div class="flex items-center justify-between gap-2">
            <div class="flex flex-col">
                <p
                    class="text-xs font-semibold uppercase tracking-wide text-slate-500"
                >
                    {{ 'translate_chats' | translate }}
                </p>
                <p class="text-[0.65rem] text-slate-400">
                    {{
                        'translate_select_conversation_or_start_new_one'
                            | translate
                    }}
                </p>
            </div>
            <button
                (click)="startNewChat()"
                class="btn btn-sm btn-primary inline-flex items-center gap-1 rounded-full text-xs"
            >
                <i class="fa fa-plus"></i>
                <span class="hidden sm:inline">
                    {{ 'translate_new_chat' | translate }}
                </span>
            </button>
        </div>

        <div class="flex grow flex-col overflow-y-auto">
            @if (!chatService.chats?.length) {
                <div
                    class="mt-6 rounded-xl border border-dashed border-slate-300 bg-white/60 p-4 text-center text-xs text-slate-400"
                >
                    {{
                        'translate_no_chats_yet_start_new_conversation'
                            | translate
                    }}
                </div>
            }

            <div class="mt-2 grid grid-cols-1 gap-2 lg:grid-cols-3">
                @for (chat of chatService.chats; track chat) {
                    <div
                        (click)="openChat(chat)"
                        class="group flex cursor-pointer select-none items-center justify-between gap-2 rounded-xl border border-transparent bg-white/70 px-3 py-2 text-xs text-slate-700 shadow-sm transition hover:-translate-y-[1px] hover:border-primary-100 hover:bg-white hover:shadow-md"
                        [ngClass]="{
                            'bg-white ring-1 ring-primary-200':
                                chatService.selection?.chat === chat,
                        }"
                    >
                        <div class="flex min-w-0 flex-col">
                            <p
                                class="truncate text-[0.8rem] font-medium text-slate-800"
                            >
                                {{ chat.title }}
                            </p>
                            <p
                                class="mt-0.5 line-clamp-2 text-[0.7rem] text-slate-400"
                            >
                                {{
                                    'translate_open_to_continue_this_conversation'
                                        | translate
                                }}
                            </p>
                        </div>
                        <button
                            class="flex h-6 w-6 items-center justify-center rounded text-slate-400 opacity-0 transition hover:bg-rose-50 hover:text-rose-600 group-hover:opacity-100"
                            (click)="
                                $event.stopPropagation();
                                chatService.deleteChat(chat)
                            "
                        >
                            <i class="fa-light fa-trash text-xs"></i>
                        </button>
                    </div>
                }
            </div>
        </div>
    </div>
    <!-- Dark overlay over chat list when panel is open -->
    <div
        class="absolute inset-0 z-10 bg-black/40 transition-opacity duration-200"
        [ngClass]="{
            'pointer-events-none opacity-0': !isPanelOpen,
            'pointer-events-auto opacity-100': isPanelOpen,
        }"
        (click)="isPanelOpen ? closeChatPanel() : null"
    ></div>

    <!-- Sliding chat panel -->
    <div
        class="pointer-events-auto absolute inset-y-0 z-20 flex w-[88%] max-w-full transform bg-white shadow-2xl transition-transform duration-300 ease-out ltr:right-0 rtl:left-0"
        [ngClass]="
            isPanelOpen
                ? 'translate-x-0'
                : 'ltr:translate-x-full rtl:-translate-x-full'
        "
    >
        <div class="flex h-full w-full flex-col">
            <!-- Top bar -->
            <div
                class="flex items-center justify-between gap-2 border-b border-slate-200 bg-white/80 px-4 py-2 text-sm font-medium text-slate-700 backdrop-blur"
            >
                <div class="flex min-w-0 items-center gap-2">
                    <button
                        type="button"
                        class="inline-flex h-7 w-7 items-center justify-center rounded-full border border-slate-200 text-[0.65rem] text-slate-500 hover:bg-slate-100"
                        (click)="closeChatPanel()"
                    >
                        <i class="fa fa-chevron-left ltr:inline rtl:hidden"></i>
                        <i
                            class="fa fa-chevron-right ltr:hidden rtl:inline"
                        ></i>
                    </button>
                    <!-- Avatar -->
                    <div
                        class="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 text-xs font-semibold uppercase text-white"
                    >
                        {{
                            chatService.selection?.chat?.title || 'AI'
                                | slice: 0 : 2
                        }}
                    </div>

                    <!-- Chat title -->
                    <div class="flex min-w-0 flex-col">
                        <!-- Title -->
                        <span class="truncate text-[0.85rem]">
                            @if (chatService.selection) {
                                {{ chatService.selection.chat.title }}
                            } @else {
                                {{ 'translate_new_chat' | translate }}
                            }
                        </span>

                        <!-- Streaming indicator -->
                        @if (chatService.isStreaming) {
                            <div
                                class="flex items-center gap-2 text-[0.65rem] text-slate-400"
                            >
                                <app-loading-ring />
                                <span>
                                    {{
                                        'translate_streaming_response'
                                            | translate
                                    }}
                                </span>
                            </div>
                        }
                    </div>
                </div>
                <button
                    type="button"
                    class="hidden rounded-full border border-slate-200 px-3 py-1 text-[0.65rem] font-medium text-slate-500 hover:bg-slate-100 sm:inline-flex"
                    (click)="startNewChat()"
                >
                    {{ 'translate_new_chat' | translate }}
                </button>
            </div>

            <!-- Messages -->
            <div
                #scrollContainer
                class="flex grow flex-col gap-2 overflow-y-auto bg-slate-50 px-4 py-3"
            >
                <app-chat-message-list
                    [messages]="chatService.selection?.messages ?? []"
                />
                @for (stream of chatService.streams; track stream) {
                    <app-chat-message-list [messages]="stream.messages" />
                }
            </div>

            <!-- Input -->
            <div
                class="border-t border-slate-200 bg-white/90 px-4 py-3 backdrop-blur"
            >
                <div class="flex items-center gap-3">
                    <textarea
                        [(ngModel)]="prompt"
                        [disabled]="!chatService.canSend"
                        rows="3"
                        (keyup)="checkIfEnter($event)"
                        class="max-h-40 min-h-[3rem] w-full resize-none rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm shadow-sm outline-none transition focus:border-primary-500 focus:ring-2 focus:ring-primary-100 disabled:bg-slate-100 disabled:text-slate-400"
                        placeholder="{{
                            'translate_write_your_message_here' | translate
                        }}"
                    ></textarea>
                    <button
                        (click)="
                            chatService.isStreaming
                                ? chatService.stopChat()
                                : !chatService.isProcessing
                                  ? sendMessage()
                                  : undefined
                        "
                        class="btn btn-primary flex shrink-0 items-center justify-center gap-2 rounded-full px-4 py-2 text-sm"
                        [disabled]="
                            chatService.isProcessing && !chatService.isStreaming
                        "
                    >
                        <i
                            class="fa"
                            [ngClass]="{
                                'fa-paper-plane': !chatService.isStreaming,
                                'fa-square': chatService.isStreaming,
                            }"
                        ></i>
                        <span class="hidden sm:inline">
                            {{
                                chatService.isStreaming
                                    ? ('translate_stop' | translate)
                                    : ('translate_send' | translate)
                            }}
                        </span>
                        @if (
                            chatService.isProcessing && !chatService.isStreaming
                        ) {
                            <app-loading-ring />
                        }
                    </button>
                </div>
                <p class="mt-1 text-[0.65rem] text-slate-400">
                    {{ 'translate_press_enter_to_send' | translate }}
                </p>
            </div>
        </div>
    </div>
</div>
